import type { UserNpcConfig } from '../types/npc';
import type { SceneDocument } from '../types/scene';

export const TASK_TOOL_NAVIGATION_FALLBACK_WINDOW_MS = 45_000;

const NAMED_NAVIGATION_TARGETS: Record<string, { x: number; y: number }> = {
  town_square: { x: 10, y: 26 },
  forest_gate: { x: 33, y: 16 },
  library: { x: 31, y: 40 },
  operations: { x: 31, y: 30 },
  support_desk: { x: 31, y: 56 },
};

export type ActivePlacementNpc = {
  id: string;
  worldNpcId: string;
  name: string;
  persistToOwnedScene: boolean;
  definition: UserNpcConfig;
};

const mergeBuiltInSceneWithFallback = (
  loadedScene: SceneDocument,
  fallbackScene: SceneDocument,
): SceneDocument => {
  if (loadedScene.ownerId !== 'system') {
    return loadedScene;
  }

  return {
    ...fallbackScene,
    ...loadedScene,
    backgroundUrl: fallbackScene.backgroundUrl,
    foregroundUrl: fallbackScene.foregroundUrl,
    traversalMaskUrl: fallbackScene.traversalMaskUrl,
    thumbnailUrl: fallbackScene.thumbnailUrl,
  };
};

export const mergeGalleryScenes = (
  loadedScenes: SceneDocument[],
  fallbackScenes: SceneDocument[],
): SceneDocument[] => {
  const fallbackById = new Map<string, SceneDocument>();
  const byId = new Map<string, SceneDocument>();
  fallbackScenes.forEach((scene) => {
    fallbackById.set(scene.id, scene);
    byId.set(scene.id, scene);
  });
  loadedScenes.forEach((scene) => {
    const fallbackScene = fallbackById.get(scene.id);
    byId.set(scene.id, fallbackScene ? mergeBuiltInSceneWithFallback(scene, fallbackScene) : scene);
  });
  return [...byId.values()];
};

export const parseFallbackNavigationTarget = (
  targetArg: unknown,
  resultText: string,
): { x: number; y: number } | null => {
  if (targetArg && typeof targetArg === 'object' && !Array.isArray(targetArg)) {
    const candidate = targetArg as Record<string, unknown>;
    const x = Number(candidate.x);
    const y = Number(candidate.y);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return { x: Math.floor(x), y: Math.floor(y) };
    }
  }

  if (typeof targetArg === 'string') {
    return NAMED_NAVIGATION_TARGETS[targetArg.trim().toLowerCase()] ?? null;
  }

  const match = resultText.match(/Navigating to \(([-\d.]+),\s*([-\d.]+)\) at speed ([\d.]+)/i);
  if (!match) {
    return null;
  }

  const parsedX = Number(match[1]);
  const parsedY = Number(match[2]);
  if (!Number.isFinite(parsedX) || !Number.isFinite(parsedY)) {
    return null;
  }

  return {
    x: Math.floor(parsedX),
    y: Math.floor(parsedY),
  };
};
